import { CustomerForecastFilterItem as GetCustomerForecastFilterItemParameter } from '@/coreInfrastructureModule/axios/generated/customerForecastManage';
import { FilterType, isFetchRequiredFilterType } from '../entities/filter';
import { FilterContentItem, isDateRangeFilterType } from '@/corePagesModule/filterModal/filterModal';
import { UseFilterItemForOrderControlManage } from '@/coreInfrastructureModule/axios/generated/orderControlManage/api';
import { JsonService } from './jsonService';
import { convertLeadTimeToDays } from '../leadtime';
import { convertDateUnitType } from '../date';
import { UNASSIGNED_COLUMN } from '../constants';
import { IOrderStatusFilterItem } from '../entities/order';

/**
 * 共通のフィルター機能のサービス
 */
export class FilterService {
  /**
   * フィルタータイプからキーを取得する
   * @param filterType - フィルタータイプ
   */
  public static getKeyByFilterType(filterType: FilterType): string {
    if (!isFetchRequiredFilterType(filterType)) {
      // フェッチが不要なフィルタータイプの場合はそのまま返す
      return filterType;
    }

    switch (filterType) {
      case 'SUPPLIER_NAME':
        return 'supplierName';
      case 'BUSINESS_NAME':
        return 'businessName';
      case 'PACKAGE_AMOUNT':
        return 'package';
      case 'UNIT':
        return 'unit';
      case 'WAREHOUSE':
        return 'warehouse';
      case 'BUSINESS_CODE':
        return 'businessNumber';
      case 'WAREHOUSE_CODE':
        return 'warehouseNumber';
      case 'SUPPLIER_CODE':
        return 'supplierNumber';
      case 'LEAD_TIME':
        return 'leadTime';
      case 'STANDARD_INVENTORY_INDEX_MIN':
        return 'stdInventoryIndicesMin';
      case 'STANDARD_INVENTORY_INDEX_MAX':
        return 'stdInventoryIndicesMax';
      case 'MOQ':
        return 'moq';
      case 'MANUFACTURER':
        return 'manufacturer';
      case 'SUPPLIER_FORECAST_RESPONSIBLE_USER':
        return 'aggregateBusinessResponsibleUserId';
      case 'CUSTOMER_NAME':
        return 'customerName';
      case 'CUSTOMER_CODE':
        return 'customerNumber';
      case 'SALES_DESTINATION_NAME':
        return 'salesDestinationName';
      case 'SALES_DESTINATION_CODE':
        return 'salesDestinationNumber';
      case 'END_USER':
        return 'endUser';
      case 'CUSTOMER_FORECAST_RESPONSIBLE_USER':
        return 'baseBusinessResponsibleUserId';
      case 'FORECAST_FOR_RECEIPT_NAME':
        return 'forecastForReceiptName';
      case 'FORECAST_FOR_RECEIPT_RESPONSIBLE_USER':
        return 'forecastForReceiptResponsibleUserId';
      case 'ORDER_ID':
        return 'orderId';
      case 'ORDER_DATE':
        return 'createdOrderDate';
      case 'DESIRED_DELIVERY_DATE':
        return 'desiredDeliveryDate';
      case 'ORDER_NUMBER':
        return 'orderNumber';
      case 'DELIVERY_DATE':
        return 'estimatedTimeOfArrival';
      case 'ORDER_AMOUNT':
        return 'orderAmount';
      case 'GROUP_ID':
        return 'groupId';
      case 'STAFF_NAME':
        return 'customerForecastResponsibleUserId';
      case 'FORECAST_FOR_RECEIPT_ID':
        return 'forecastForReceiptId';
      case 'ORDER_PACKAGE':
        return 'orderPackage';
      case 'ORDER_GROUP_ID':
        return 'orderGroupId';
      case 'AGGREGATE_BUSINESS_GROUP_FILTER_INFO':
        return 'groupId';
      case 'ORDER_GROUP_NAME':
        return 'orderGroupName';
      case 'INVOICE_NUMBER':
        return 'invoiceNumber';
      case 'DESTINATION_NAME':
        return 'destinationName';
      case 'UNMATCHED_BUSINESS_NAME':
        return 'businessName';
      case 'UNMATCHED_BUSINESS_NUMBER':
        return 'businessNumber';
      case 'UNMATCHED_SUPPLIER_NAME':
        return 'supplierName';
      case 'UNMATCHED_SUPPLIER_NUMBER':
        return 'supplierNumber';
      case 'UNMATCHED_WAREHOUSE':
        return 'warehouse';
      case 'UNMATCHED_WAREHOUSE_NUMBER':
        return 'warehouseNumber';
      case 'UNMATCHED_ORDER_NUMBER':
        return 'orderNumber';
      case 'UNMATCHED_ORDER_ISSUED_NUMBER':
        return 'orderIssuedNumber';
      default:
        return filterType;
    }
  }

  /**
   * 内示のフィルターアイテムをAPIのパラメータ用に変換する
   * @param forecastFilterItems - 内示のフィルターアイテム
   */
  public static convertCustomerForecastFilterItems(forecastFilterItems: FilterContentItem[]): GetCustomerForecastFilterItemParameter {
    const result: GetCustomerForecastFilterItemParameter = {};
    const dateRangeItems = forecastFilterItems.filter((item) => isDateRangeFilterType(item.filterType));
    const filteredItems = forecastFilterItems.filter((item) => !isDateRangeFilterType(item.filterType));

    filteredItems.forEach((item) => {
      const key = FilterService.getKeyByFilterType(item.filterType);
      const resKey = item.selectedValue.map((selected) => {
        if (selected === UNASSIGNED_COLUMN) {
          return encodeURIComponent('');
        } else if (item.filterType === 'LEAD_TIME') {
          const [leadTime, leadTimeUnit] = selected.split(/[0-9.]+/)[1];
          return encodeURIComponent(convertLeadTimeToDays(parseFloat(leadTime), leadTimeUnit));
        } else if (item.filterType === 'STANDARD_INVENTORY_INDEX_MIN' || item.filterType === 'STANDARD_INVENTORY_INDEX_MAX') {
          return encodeURIComponent(parseFloat(selected));
        } else if (
          item.filterType === 'CUSTOMER_FORECAST_RESPONSIBLE_USER' ||
          item.filterType === 'SUPPLIER_FORECAST_RESPONSIBLE_USER' ||
          item.filterType === 'FORECAST_FOR_RECEIPT_RESPONSIBLE_USER' ||
          item.filterType === 'AGGREGATE_BUSINESS_GROUP_FILTER_INFO'
        ) {
          return encodeURIComponent(selected.split(',')[1]);
        } else {
          return encodeURIComponent(selected);
        }
      });
      result[key] = resKey.join(',');
    });

    Object.assign(result, FilterService.convertDateRangeFilters(dateRangeItems));
    return result;
  }

  /**
   * 発注管理のフィルターアイテムをAPIのパラメータ用に変換する
   * @param orderControlFilterItems - 発注管理のフィルターアイテム
   */
  public static convertOrderControlFilterItems(orderControlFilterItems: FilterContentItem[]): UseFilterItemForOrderControlManage {
    const result: UseFilterItemForOrderControlManage = {};
    const [lastCheckedAtFilterItem] = orderControlFilterItems.filter((item) => item.filterType === 'LAST_CHECKED_AT');
    const dateRangeItems = orderControlFilterItems.filter((item) => isDateRangeFilterType(item.filterType));
    const filteredItems = orderControlFilterItems.filter(
      (item) => item.filterType !== 'LAST_CHECKED_AT' && !isDateRangeFilterType(item.filterType)
    );

    filteredItems.forEach((item) => {
      const key = FilterService.getKeyByFilterType(item.filterType);
      const resKey = item.selectedValue.map((selected) => {
        if (selected === UNASSIGNED_COLUMN) {
          return encodeURIComponent('');
        } else if (
          item.filterType === 'LEAD_TIME' ||
          item.filterType === 'STANDARD_INVENTORY_INDEX_MIN' ||
          item.filterType === 'STANDARD_INVENTORY_INDEX_MAX'
        ) {
          return encodeURIComponent(parseFloat(selected));
        } else if (
          item.filterType === 'CUSTOMER_FORECAST_RESPONSIBLE_USER' ||
          item.filterType === 'SUPPLIER_FORECAST_RESPONSIBLE_USER' ||
          item.filterType === 'FORECAST_FOR_RECEIPT_RESPONSIBLE_USER' ||
          item.filterType === 'AGGREGATE_BUSINESS_GROUP_FILTER_INFO'
        ) {
          return encodeURIComponent(selected.split(',')[1]);
        } else {
          return encodeURIComponent(selected);
        }
      });
      result[key] = resKey.join(',');
    });

    Object.assign(result, FilterService.convertLastCheckedAtFilter(lastCheckedAtFilterItem));
    Object.assign(result, FilterService.convertDateRangeFilters(dateRangeItems));
    return result;
  }

  /**
   * 最終チェック日時のフィルター情報をAPIのクエリ形式に変換する。
   * @param filterItem - 最終チェック日時のフィルター項目
   * @returns クエリパラメータ形式のオブジェクト
   */
  public static convertLastCheckedAtFilter(filterItem?: FilterContentItem): Record<string, string> {
    const result: Record<string, string> = {};
    if (!filterItem) {
      return result;
    }
    const includeNull = filterItem.includeNull ?? false;
    if (filterItem.selectedValue.length === 0 && !includeNull) {
      return result;
    }
    const [from, to] = filterItem.selectedValue;
    if (from) {
      result.lastCheckedAtFrom = encodeURIComponent(from);
    }
    if (to) {
      result.lastCheckedAtTo = encodeURIComponent(to);
    }
    if (includeNull) {
      result.lastCheckedAtIncludeNull = encodeURIComponent('true');
    }
    return result;
  }

  /**
   * 日付範囲フィルターの情報をAPIのクエリ形式に変換する。
   * selectedValue[0] = from (ms), selectedValue[1] = to (ms)
   * @param filterItem - 日付範囲フィルター項目
   * @returns クエリパラメータ形式のオブジェクト
   */
  public static convertDateRangeFilter(filterItem: FilterContentItem): Record<string, string> {
    const result: Record<string, string> = {};
    const includeNull = filterItem.includeNull ?? false;
    if (filterItem.selectedValue.length === 0 && !includeNull) {
      return result;
    }
    const baseKey = FilterService.getKeyByFilterType(filterItem.filterType);
    const [from, to] = filterItem.selectedValue;
    if (from) {
      result[`${baseKey}From`] = encodeURIComponent(from);
    }
    if (to) {
      result[`${baseKey}To`] = encodeURIComponent(to);
    }
    if (includeNull) {
      result[`${baseKey}IncludeNull`] = encodeURIComponent('true');
    }
    return result;
  }

  /**
   * 日付範囲フィルター項目をまとめてAPIのクエリ形式に変換する。
   * @param filterItems - 日付範囲フィルター項目の配列
   * @returns クエリパラメータ形式のオブジェクト
   */
  public static convertDateRangeFilters(filterItems: FilterContentItem[]): Record<string, string> {
    const result: Record<string, string> = {};
    filterItems.forEach((item) => {
      Object.assign(result, FilterService.convertDateRangeFilter(item));
    });
    return result;
  }

  /**
   * 発注ステータスフィルタ項目をAPIのクエリ形式に変換する
   * @param filterItem - 発注ステータスフィルタ項目
   * @returns - クエリパラメータ形式の発注ステータスフィルタ項目
   */
  public static convertOrderStatusFilter(filterItem?: IOrderStatusFilterItem): string | undefined {
    if (!filterItem) return undefined;
    return Object.entries(filterItem)
      .filter(([_, v]) => v)
      .map(([k, _]) => encodeURIComponent(k))
      .join(',');
  }

  /**
   * フィルターアイテムをkey, valueの形式に変換する
   * @param filterItems - フィルターアイテム
   */
  public static convertFilterObject(filterItems: FilterContentItem[]): { [key: string]: string } {
    const [lastCheckedAtFilterItem] = filterItems.filter((item) => item.filterType === 'LAST_CHECKED_AT');
    const dateRangeItems = filterItems.filter((item) => isDateRangeFilterType(item.filterType));
    const filteredItems = filterItems.filter((item) => item.filterType !== 'LAST_CHECKED_AT' && !isDateRangeFilterType(item.filterType));

    const convertedFilter = filteredItems.reduce((acc, item) => {
      const key = FilterService.getKeyByFilterType(item.filterType);
      acc[key] = item.selectedValue
        .map((selected) => {
          if (selected === UNASSIGNED_COLUMN) {
            return encodeURIComponent('');
          } else if (
            item.filterType === 'LEAD_TIME' ||
            item.filterType === 'STANDARD_INVENTORY_INDEX_MIN' ||
            item.filterType === 'STANDARD_INVENTORY_INDEX_MAX'
          ) {
            return encodeURIComponent(parseFloat(selected));
          } else if (
            item.filterType === 'CUSTOMER_FORECAST_RESPONSIBLE_USER' ||
            item.filterType === 'SUPPLIER_FORECAST_RESPONSIBLE_USER' ||
            item.filterType === 'FORECAST_FOR_RECEIPT_RESPONSIBLE_USER' ||
            item.filterType === 'AGGREGATE_BUSINESS_GROUP_FILTER_INFO'
          ) {
            return encodeURIComponent(selected.split(',')[1]);
          } else {
            return encodeURIComponent(selected);
          }
        })
        .join(',');
      return acc;
    }, {});

    return {
      ...convertedFilter,
      ...FilterService.convertLastCheckedAtFilter(lastCheckedAtFilterItem),
      ...FilterService.convertDateRangeFilters(dateRangeItems),
    };
  }
  /**
   * フィルターアイテムをAPI用の形式に変換する
   * リードタイムと標準在庫指数: value, unitをまとめたオブジェクト
   * それ以外: key. valueの形式
   * @param filterItems -フィルターアイテム
   * @returns - convertedFilterItems: フィルタ情報（リードタイムと標準在庫指数除く）, convertedObjectFilterItems: リードタイムと標準在庫指数のフィルタ情報
   */
  public static convertFilterObjectWithInfo(filterItems: FilterContentItem[]): { [key: string]: string }[] {
    const [lastCheckedAtFilterItem] = filterItems.filter((item) => item.filterType === 'LAST_CHECKED_AT');
    const dateRangeItems = filterItems.filter((item) => isDateRangeFilterType(item.filterType));
    const filteredItems = filterItems.filter((item) => item.filterType !== 'LAST_CHECKED_AT' && !isDateRangeFilterType(item.filterType));
    const [stringFilterItems, leadTimeFilterItems, stdInventoryIndexMinFilterItems, stdInventoryIndexMaxFilterItems] = filteredItems.reduce(
      ([stringItems, leadTimeFilterItems, stdInventoryIndexMinFilterItems, stdInventoryIndexMaxFilterItems], item) => {
        if (item.filterType === 'LEAD_TIME') {
          leadTimeFilterItems.push(item);
        } else if (item.filterType === 'STANDARD_INVENTORY_INDEX_MIN') {
          stdInventoryIndexMinFilterItems.push(item);
        } else if (item.filterType === 'STANDARD_INVENTORY_INDEX_MAX') {
          stdInventoryIndexMaxFilterItems.push(item);
        } else {
          stringItems.push(item);
        }
        return [stringItems, leadTimeFilterItems, stdInventoryIndexMinFilterItems, stdInventoryIndexMaxFilterItems];
      },
      [[], [], [], []] as [FilterContentItem[], FilterContentItem[], FilterContentItem[], FilterContentItem[]]
    );

    const convertedFilterItems = stringFilterItems.reduce((acc, item) => {
      const key = FilterService.getKeyByFilterType(item.filterType);
      acc[key] = item.selectedValue
        .map((selected) => {
          if (selected === UNASSIGNED_COLUMN) {
            return encodeURIComponent('');
          } else if (
            item.filterType === 'CUSTOMER_FORECAST_RESPONSIBLE_USER' ||
            item.filterType === 'SUPPLIER_FORECAST_RESPONSIBLE_USER' ||
            item.filterType === 'FORECAST_FOR_RECEIPT_RESPONSIBLE_USER' ||
            item.filterType === 'AGGREGATE_BUSINESS_GROUP_FILTER_INFO'
          ) {
            return encodeURIComponent(selected.split(',')[1]);
          } else {
            return encodeURIComponent(selected);
          }
        })
        .join(',');
      return acc;
    }, {});

    const convertedObjectFilterItems = {
      leadTimeInfoList: leadTimeFilterItems.length
        ? JsonService.convertObjectToCompressedJson(FilterService._convertStringToInfo(leadTimeFilterItems[0]?.selectedValue, true))
        : '',
      stdInventoryIndexMinInfoList: stdInventoryIndexMinFilterItems.length
        ? JsonService.convertObjectToCompressedJson(FilterService._convertStringToInfo(stdInventoryIndexMinFilterItems[0]?.selectedValue))
        : '',
      stdInventoryIndexMaxInfoList: stdInventoryIndexMaxFilterItems.length
        ? JsonService.convertObjectToCompressedJson(FilterService._convertStringToInfo(stdInventoryIndexMaxFilterItems[0]?.selectedValue))
        : '',
    };
    return [
      {
        ...convertedFilterItems,
        ...FilterService.convertLastCheckedAtFilter(lastCheckedAtFilterItem),
        ...FilterService.convertDateRangeFilters(dateRangeItems),
      },
      convertedObjectFilterItems,
    ];
  }

  /**
   * 1.5monthのような値を数字と単位に分ける
   * @param value - 値
   */
  public static separateStringValueAndUnit = (value: string) => {
    const match = value.match(/^([0-9]+(?:\.[0-9]+)?)\s*(.+)$/);
    if (match) {
      return {
        value: match[1],
        unit: match[2],
      };
    } else {
      // valueが1.5monthのような形式でない
      return null;
    }
  };

  /**
   * 値と単位が結合した文字列の配列からそれぞれを取り出してオブジェクトの配列に格納して返す関数
   * @param selectedValue - 値と単位が結合した文字列
   * @param isNeedCalculate - バックエンド用に日付の変更が必要かどうか
   * @returns - 値と単位を持つオブジェクトの配列
   */
  private static _convertStringToInfo(selectedValue: string[], isNeedCalculate = false) {
    return selectedValue.reduce((acc, value) => {
      const match = this.separateStringValueAndUnit(value);
      if (match) {
        const value = parseFloat(match.value);
        const unit = convertDateUnitType(match.unit);
        acc.push({
          value: isNeedCalculate ? convertLeadTimeToDays(value, unit) : value,
          unit: unit,
        });
      }
      return acc;
    }, [] as object[]);
  }
}
